import { Component, computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, of } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';

import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';
import { MarkdownPreviewComponent } from '../../shared/components/markdown-preview/markdown-preview.component';
import { RulesService } from '../../core/services/rules.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { PdfExtractorService } from '../../core/services/pdf-extractor.service';
import {
  BrdStatus,
  BulkApplyRulePayload,
  ProjectBrd,
  ProjectRule,
  RuleCategory,
  RulePriority,
  UpsertBrdPayload,
  UpsertRulePayload,
} from '../../core/models/rules.models';
import { RepositoryListItem } from '../../core/models/repository.models';

const EMPTY_RULE: UpsertRulePayload = { repositoryId: null, categoryId: 0, title: '', ruleText: '', priority: 2, isActive: true };
const EMPTY_BRD: UpsertBrdPayload = { repositoryId: null, title: '', content: '', version: '' };

type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';

interface RepoOption {
  id: string;
  name: string;
}

interface ScopeOption {
  id: string | null;
  label: string;
}

interface RepoSelection {
  repoId: string;
  repoName: string;
  projectId: string;
  projectName: string;
  projectLabel: string;
  organization: string;
  siblingRepos: RepoOption[];
}

interface ProjectOption {
  projectId: string;
  projectName: string;
  organization: string;
  label: string;
}

@Component({
  selector: 'app-rules-page',
  standalone: true,
  imports: [
    FormsModule,
    LocalDatePipe,
    MarkdownPreviewComponent,
    ButtonModule,
    TableModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    TextareaModule,
    TagModule,
    CheckboxModule,
    TooltipModule,
    MessageModule,
  ],
  templateUrl: './rules-page.component.html',
  styleUrl: './rules-page.component.scss',
})
export class RulesPageComponent {
  private readonly rulesService = inject(RulesService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly pdfExtractor = inject(PdfExtractorService);
  private readonly destroyRef = inject(DestroyRef);

  readonly repositories = signal<RepoSelection[]>([]);
  readonly selectedRepo = signal<RepoSelection | null>(null);

  readonly activeTab = signal<'rules' | 'brds'>('rules');

  readonly categories = signal<RuleCategory[]>([]);
  readonly selectedCategory = signal<RuleCategory | null>(null);
  readonly selectedPriority = signal<{ value: number; label: RulePriority } | null>(null);

  readonly rules = signal<ProjectRule[]>([]);
  readonly brds = signal<ProjectBrd[]>([]);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);

  readonly showRuleForm = signal(false);
  readonly ruleForm = signal<UpsertRulePayload>({ ...EMPTY_RULE });
  readonly additionalProjectIds = signal<string[]>([]);

  readonly showCopyPanel = signal(false);
  readonly copySourceProjectId = signal<string | null>(null);
  readonly copySourceRules = signal<ProjectRule[]>([]);
  readonly copySelectedRuleIds = signal<Set<string>>(new Set());
  readonly copyTargetRepositoryId = signal<string | null>(null);
  readonly copyLoading = signal(false);
  readonly copySaving = signal(false);
  readonly copyError = signal<string | null>(null);

  readonly showBrdForm = signal(false);
  readonly brdForm = signal<UpsertBrdPayload>({ ...EMPTY_BRD });

  readonly approvingBrdId = signal<string | null>(null);
  readonly approveByInput = signal('');

  readonly pdfLoading = signal(false);
  readonly pdfError = signal<string | null>(null);
  readonly pdfProgress = this.pdfExtractor.progress;

  pdfProgressLabel(): string {
    const p = this.pdfProgress();
    switch (p.stage) {
      case 'loading-pdf': return 'Loading PDF…';
      case 'rendering':   return `Rendering page ${p.page} of ${p.total}…`;
      case 'recognizing': return `Reading page ${p.page} of ${p.total} (${p.percent}%)`;
      case 'formatting':  return 'Formatting…';
      default:            return 'Extracting…';
    }
  }

  readonly brdFormTab = signal<'edit' | 'preview'>('edit');
  readonly viewingBrdId = signal<string | null>(null);

  switchBrdFormTab(tab: 'edit' | 'preview'): void {
    this.brdFormTab.set(tab);
  }

  toggleBrdView(brd: ProjectBrd): void {
    this.viewingBrdId.set(this.viewingBrdId() === brd.id ? null : brd.id);
  }

  readonly priorityOptions: { value: number; label: RulePriority }[] = [
    { value: 4, label: 'Critical' },
    { value: 3, label: 'High' },
    { value: 2, label: 'Medium' },
    { value: 1, label: 'Low' },
  ];

  constructor() {
    this.loadRepositories();
    this.loadCategories();
    effect(() => {
      const repo = this.selectedRepo();
      const tab = this.activeTab();
      if (!repo) return;
      untracked(() => (tab === 'rules' ? this.loadRules() : this.loadBrds()));
    });
  }

  get selectedProjectId(): string {
    return this.selectedRepo()?.projectId ?? '';
  }

  get selectedRepoId(): string | null {
    return this.selectedRepo()?.repoId ?? null;
  }

  selectRepo(repo: RepoSelection | null): void {
    this.selectedRepo.set(repo);
    this.showRuleForm.set(false);
    this.showBrdForm.set(false);
    this.showCopyPanel.set(false);
    this.error.set(null);
    this.saveError.set(null);
  }

  switchTab(tab: 'rules' | 'brds'): void {
    this.activeTab.set(tab);
    this.showRuleForm.set(false);
    this.showBrdForm.set(false);
    this.saveError.set(null);
  }

  prioritySeverity(priority: RulePriority): TagSeverity {
    const map: Record<RulePriority, TagSeverity> = {
      Critical: 'danger',
      High: 'warn',
      Medium: 'info',
      Low: 'secondary',
    };
    return map[priority] ?? 'secondary';
  }

  brdStatusSeverity(status: BrdStatus): TagSeverity {
    const map: Record<BrdStatus, TagSeverity> = {
      Approved: 'success',
      UnderReview: 'warn',
      Draft: 'secondary',
      Deprecated: 'danger',
    };
    return map[status] ?? 'secondary';
  }

  brdStatusLabel(status: BrdStatus): string {
    const labels: Record<BrdStatus, string> = {
      Draft: 'Draft',
      UnderReview: 'Under Review',
      Approved: 'Approved',
      Deprecated: 'Deprecated',
    };
    return labels[status] ?? status;
  }

  // ─── Rule CRUD ────────────────────────────────────────────────────────────

  openAddRule(): void {
    this.selectedCategory.set(null);
    this.selectedPriority.set(this.priorityOptions[2]);
    this.ruleForm.set({ ...EMPTY_RULE, repositoryId: this.selectedRepoId });
    this.additionalProjectIds.set([]);
    this.showRuleForm.set(true);
    this.showCopyPanel.set(false);
    this.saveError.set(null);
  }

  openEditRule(rule: ProjectRule): void {
    this.selectedCategory.set(this.categories().find((c) => c.id === rule.categoryId) ?? null);
    this.selectedPriority.set(this.priorityOptions.find((p) => p.value === this.priorityToNum(rule.priority)) ?? null);
    this.ruleForm.set({
      id: rule.id,
      repositoryId: rule.repositoryId,
      categoryId: rule.categoryId,
      title: rule.title,
      ruleText: rule.ruleText,
      priority: this.priorityToNum(rule.priority),
      isActive: rule.isActive,
    });
    this.additionalProjectIds.set([]);
    this.showRuleForm.set(true);
    this.showCopyPanel.set(false);
    this.saveError.set(null);
  }

  cancelRuleForm(): void {
    this.showRuleForm.set(false);
    this.saveError.set(null);
  }

  patchRule<K extends keyof UpsertRulePayload>(key: K, value: UpsertRulePayload[K]): void {
    this.ruleForm.update((f) => ({ ...f, [key]: value }));
  }

  saveRule(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;
    this.saving.set(true);
    this.saveError.set(null);

    const form = this.ruleForm();
    const extras = form.id ? [] : this.additionalProjectIds().filter((id) => id && id !== projectId);

    this.rulesService
      .upsertRule(projectId, form)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          const list = this.rules();
          const idx = list.findIndex((r) => r.id === saved.id);
          this.rules.set(idx >= 0 ? [...list.slice(0, idx), saved, ...list.slice(idx + 1)] : [...list, saved]);

          if (extras.length === 0) {
            this.saving.set(false);
            this.showRuleForm.set(false);
            return;
          }

          const payload: BulkApplyRulePayload = {
            projectIds: extras,
            categoryId: form.categoryId,
            title: form.title,
            ruleText: form.ruleText,
            priority: form.priority,
            isActive: form.isActive,
          };
          this.rulesService
            .bulkApplyRule(payload)
            .pipe(finalize(() => this.saving.set(false)), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.showRuleForm.set(false);
                this.additionalProjectIds.set([]);
              },
              error: (err) => this.saveError.set(this.formatError(err, 'Saved here, but could not apply to other projects.')),
            });
        },
        error: (err) => {
          this.saving.set(false);
          this.saveError.set(this.formatError(err, 'Could not save rule.'));
        },
      });
  }

  deleteRule(rule: ProjectRule): void {
    const projectId = this.selectedProjectId;
    if (!projectId || !confirm(`Delete rule "${rule.title}"?`)) return;
    this.rulesService
      .deleteRule(projectId, rule.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.rules.set(this.rules().filter((r) => r.id !== rule.id)),
        error: (err) => this.error.set(this.formatError(err, 'Could not delete rule.')),
      });
  }

  // ─── BRD CRUD ─────────────────────────────────────────────────────────────

  openAddBrd(): void {
    this.brdForm.set({ ...EMPTY_BRD, repositoryId: this.selectedRepoId });
    this.showBrdForm.set(true);
    this.brdFormTab.set('edit');
    this.saveError.set(null);
  }

  openEditBrd(brd: ProjectBrd): void {
    this.brdForm.set({
      id: brd.id,
      repositoryId: brd.repositoryId,
      title: brd.title,
      content: brd.content,
      version: brd.version,
    });
    this.showBrdForm.set(true);
    this.brdFormTab.set('edit');
    this.saveError.set(null);
  }

  cancelBrdForm(): void {
    this.showBrdForm.set(false);
    this.saveError.set(null);
  }

  patchBrd<K extends keyof UpsertBrdPayload>(key: K, value: UpsertBrdPayload[K]): void {
    this.brdForm.update((f) => ({ ...f, [key]: value }));
  }

  saveBrd(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;
    this.saving.set(true);
    this.saveError.set(null);
    this.rulesService
      .upsertBrd(projectId, this.brdForm())
      .pipe(finalize(() => this.saving.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          const list = this.brds();
          const idx = list.findIndex((b) => b.id === saved.id);
          this.brds.set(idx >= 0 ? [...list.slice(0, idx), saved, ...list.slice(idx + 1)] : [...list, saved]);
          this.showBrdForm.set(false);
        },
        error: (err) => this.saveError.set(this.formatError(err, 'Could not save BRD.')),
      });
  }

  deleteBrd(brd: ProjectBrd): void {
    const projectId = this.selectedProjectId;
    if (!projectId || !confirm(`Delete BRD "${brd.title}"?`)) return;
    this.rulesService
      .deleteBrd(projectId, brd.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.brds.set(this.brds().filter((b) => b.id !== brd.id)),
        error: (err) => this.error.set(this.formatError(err, 'Could not delete BRD.')),
      });
  }

  startApprove(brd: ProjectBrd): void {
    this.approvingBrdId.set(brd.id);
    this.approveByInput.set('');
  }

  cancelApprove(): void {
    this.approvingBrdId.set(null);
    this.approveByInput.set('');
  }

  confirmApprove(brd: ProjectBrd): void {
    const projectId = this.selectedProjectId;
    const approvedBy = this.approveByInput().trim();
    if (!projectId || !approvedBy) return;
    this.rulesService
      .approveBrd(projectId, brd.id, approvedBy)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          const list = this.brds();
          const idx = list.findIndex((b) => b.id === saved.id);
          if (idx >= 0) this.brds.set([...list.slice(0, idx), saved, ...list.slice(idx + 1)]);
          this.approvingBrdId.set(null);
          this.approveByInput.set('');
        },
        error: (err) => this.error.set(this.formatError(err, 'Could not approve BRD.')),
      });
  }

  // ─── PDF import ───────────────────────────────────────────────────────────

  async onPdfUpload(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file || file.type !== 'application/pdf') return;
    this.pdfLoading.set(true);
    this.pdfError.set(null);
    try {
      const extracted = await this.pdfExtractor.extractBrd(file);
      this.brdForm.update((f) => ({
        ...f,
        title: extracted.title || f.title,
        version: extracted.version || f.version,
        content: extracted.content,
      }));
    } catch {
      this.pdfError.set('Could not read PDF. Make sure it contains selectable text.');
    } finally {
      this.pdfLoading.set(false);
      (event.target as HTMLInputElement).value = '';
    }
  }

  // ─── Copy from another project ────────────────────────────────────────────

  readonly allProjects = computed<ProjectOption[]>(() => {
    const seen = new Map<string, ProjectOption>();
    for (const r of this.repositories()) {
      if (!seen.has(r.projectId)) {
        seen.set(r.projectId, {
          projectId: r.projectId,
          projectName: r.projectName,
          organization: r.organization,
          label: `${r.projectName} · ${r.organization}`,
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.projectName.localeCompare(b.projectName));
  });

  readonly otherProjects = computed<ProjectOption[]>(() => {
    const currentId = this.selectedProjectId;
    return this.allProjects().filter((p) => p.projectId !== currentId);
  });

  openCopyPanel(): void {
    this.showCopyPanel.set(true);
    this.showRuleForm.set(false);
    this.copySourceProjectId.set(null);
    this.copySourceRules.set([]);
    this.copySelectedRuleIds.set(new Set());
    this.copyTargetRepositoryId.set(null);
    this.copyError.set(null);
  }

  readonly currentCopyTargetScope = computed<ScopeOption | null>(() => {
    const options = this.scopeOptions();
    if (options.length === 0) return null;
    const id = this.copyTargetRepositoryId();
    return options.find((s) => s.id === id) ?? options[0];
  });

  onCopyTargetScopeChange(scope: ScopeOption | null): void {
    this.copyTargetRepositoryId.set(scope?.id ?? null);
  }

  cancelCopyPanel(): void {
    this.showCopyPanel.set(false);
    this.copyError.set(null);
  }

  onCopySourceChange(projectId: string | null): void {
    this.copySourceProjectId.set(projectId);
    this.copySelectedRuleIds.set(new Set());
    this.copySourceRules.set([]);
    if (!projectId) return;
    this.copyLoading.set(true);
    this.copyError.set(null);
    this.rulesService
      .getRules(projectId)
      .pipe(finalize(() => this.copyLoading.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => this.copySourceRules.set(data),
        error: (err) => this.copyError.set(this.formatError(err, 'Could not load source rules.')),
      });
  }

  toggleCopyRule(ruleId: string): void {
    const next = new Set(this.copySelectedRuleIds());
    if (next.has(ruleId)) next.delete(ruleId); else next.add(ruleId);
    this.copySelectedRuleIds.set(next);
  }

  isCopyRuleSelected(ruleId: string): boolean {
    return this.copySelectedRuleIds().has(ruleId);
  }

  toggleCopyAll(): void {
    const all = this.copySourceRules();
    const selected = this.copySelectedRuleIds();
    if (selected.size === all.length) {
      this.copySelectedRuleIds.set(new Set());
    } else {
      this.copySelectedRuleIds.set(new Set(all.map((r) => r.id)));
    }
  }

  get copyAllChecked(): boolean {
    const all = this.copySourceRules();
    return all.length > 0 && this.copySelectedRuleIds().size === all.length;
  }

  confirmCopy(): void {
    const targetProjectId = this.selectedProjectId;
    const sourceProjectId = this.copySourceProjectId();
    if (!targetProjectId || !sourceProjectId) return;

    const ids = Array.from(this.copySelectedRuleIds());
    const targetRepositoryId = this.copyTargetRepositoryId();
    this.copySaving.set(true);
    this.copyError.set(null);
    this.rulesService
      .copyRules(targetProjectId, {
        sourceProjectId,
        ruleIds: ids.length > 0 ? ids : null,
        targetRepositoryId,
      })
      .pipe(finalize(() => this.copySaving.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (copied) => {
          if (copied.length > 0) this.rules.set([...this.rules(), ...copied]);
          this.showCopyPanel.set(false);
        },
        error: (err) => this.copyError.set(this.formatError(err, 'Could not copy rules.')),
      });
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private loadRepositories(): void {
    this.analyticsService
      .getRepositories({ page: 1, pageSize: 100 })
      .pipe(
        catchError(() => of({ items: [], paging: { page: 1, pageSize: 100, totalCount: 0, totalPages: 0 } })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.repositories.set(this.buildRepoList(result.items)));
  }

  private buildRepoList(repos: RepositoryListItem[]): RepoSelection[] {
    const reposByProjectId = new Map<string, RepoOption[]>();
    for (const r of repos) {
      const list = reposByProjectId.get(r.project.id) ?? [];
      if (!list.some((x) => x.id === r.id)) list.push({ id: r.id, name: r.name });
      reposByProjectId.set(r.project.id, list);
    }
    const projectNameCounts = new Map<string, number>();
    for (const r of repos) projectNameCounts.set(r.project.name, (projectNameCounts.get(r.project.name) ?? 0) + 1);

    const selections: RepoSelection[] = repos.map((r) => {
      const siblings = (reposByProjectId.get(r.project.id) ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));
      return {
        repoId: r.id,
        repoName: r.name,
        projectId: r.project.id,
        projectName: r.project.name,
        projectLabel: r.project.name,
        organization: r.organization.name,
        siblingRepos: siblings,
      };
    });

    return selections.sort((a, b) =>
      a.projectName.localeCompare(b.projectName) || a.repoName.localeCompare(b.repoName),
    );
  }

  readonly scopeOptions = computed<ScopeOption[]>(() => {
    const repo = this.selectedRepo();
    if (!repo) return [];
    return [
      { id: null, label: 'Project-wide (all repos)' },
      ...repo.siblingRepos.map((r) => ({
        id: r.id,
        label: r.id === repo.repoId ? `${r.name} (current)` : r.name,
      })),
    ];
  });

  readonly currentRuleScope = computed<ScopeOption | null>(() => {
    const options = this.scopeOptions();
    if (options.length === 0) return null;
    const id = this.ruleForm().repositoryId ?? null;
    return options.find((s) => s.id === id) ?? options[0];
  });

  readonly currentBrdScope = computed<ScopeOption | null>(() => {
    const options = this.scopeOptions();
    if (options.length === 0) return null;
    const id = this.brdForm().repositoryId ?? null;
    return options.find((s) => s.id === id) ?? options[0];
  });

  scopeLabel(repositoryId: string | null): string {
    if (!repositoryId) return 'Project-wide';
    const repo = this.selectedRepo();
    return repo?.siblingRepos.find((r) => r.id === repositoryId)?.name ?? 'Unknown repo';
  }

  onRuleScopeChange(scope: ScopeOption | null): void {
    this.patchRule('repositoryId', scope?.id ?? null);
  }

  onBrdScopeChange(scope: ScopeOption | null): void {
    this.patchBrd('repositoryId', scope?.id ?? null);
  }

  private loadCategories(): void {
    this.rulesService
      .getCategories()
      .pipe(catchError(() => of([] as RuleCategory[])), takeUntilDestroyed(this.destroyRef))
      .subscribe((cats) => this.categories.set(cats));
  }

  private loadRules(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;
    this.loading.set(true);
    this.error.set(null);
    this.rulesService
      .getRules(projectId)
      .pipe(finalize(() => this.loading.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (data) => this.rules.set(data), error: (err) => this.error.set(this.formatError(err, 'Could not load rules.')) });
  }

  private loadBrds(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;
    this.loading.set(true);
    this.error.set(null);
    this.rulesService
      .getBrds(projectId)
      .pipe(finalize(() => this.loading.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (data) => this.brds.set(data), error: (err) => this.error.set(this.formatError(err, 'Could not load BRDs.')) });
  }

  private priorityToNum(priority: RulePriority): number {
    const map: Record<RulePriority, number> = { Low: 1, Medium: 2, High: 3, Critical: 4 };
    return map[priority] ?? 2;
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') return `${fallback} (request timed out)`;
    if (error instanceof HttpErrorResponse) return `${fallback} (${error.status}: ${error.message})`;
    return fallback;
  }
}
